import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { sendSegmentTrackEvent } from 'services/segment'
import styled from 'styled-components'

import {
  ClickableIcon,
  FlagIcon,
  LoadingIcon,
  Tooltip
} from '@junglescout/edna'

import { TEXT_STYLES } from 'TEXT_STYLES'

import { addProductAlert, removeProductAlert } from 'actions/product_alerts'

import { formatImageResolution } from 'helpers/images'
import { poll } from 'helpers/polling'

import { useNotification } from 'hooks/notification'
import { useSelectedAmazonSellerAccount } from 'hooks/amazon_seller_account'
import api from 'services/api/product'
import {
  PRODUCT_SETTINGS_CHILDREN_ENDPOINT,
  PRODUCT_SETTINGS_PARENTS_ENDPOINT
} from 'constants/api'

import { ImageStack } from 'ui_elements/ImageStack/ImageStack'
import { TruncatedTextWithTooltip } from 'ui_elements/TruncatedTextWithTooltip/TruncatedTextWithTooltip'
import { ViewRowDataButton } from 'ui_elements/ViewRowDataButton/ViewRowDataButton'

import { updateProductCostsAndSettingsCache } from './utils'

const TitleCellWrapper = styled.div`
  display: flex;
  width: 100%;
`
const StyledImageStack = styled(ImageStack)`
  margin-right: 15px;
  height: 64px;
  margin-top: auto;
  margin-bottom: auto;
  min-width: 60px;
`
const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`
const SubtitleWrapper = styled.div`
  ${TEXT_STYLES.BodyBlack};
  font-weight: 400;
  display: inline-flex;
  align-items: center;
`
const Divider = styled.div`
  height: 20px;
  border-left: 1px solid ${props => props.theme.colors.coolGrey300};
  margin: 0px 8px;
`
const StyledTruncatedText = styled(TruncatedTextWithTooltip)`
  margin-bottom: 5px;
  color: ${props => props.theme.colors.black};
`
const TitleSection = styled.div`
  width: 280px;
  margin-right: 8px;
`
const StyledASIN = styled.div`
  margin-left: 8px;
  white-space: nowrap;
`
const StyledSKU = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 130px;
  text-align: left;
`
const StyledViewRowDataButton = styled(ViewRowDataButton)`
  margin-right: 4px;
`
const ActionsWrapper = styled.div`
  display: flex;
  gap: 4px;
`
const LoadingIconWrapper = styled.div`
  padding: 4px;
`

const checkSyncComplete = response => {
  if (response?.error) {
    return response
  }

  const status = response?.data?.[0].sync_status
  if (status && status !== 1) {
    return { ...response, pollCompleted: true }
  }

  return response
}

export const ProductTitleCell = ({
  product: {
    product_id,
    asin,
    sku,
    canExpand,
    title,
    amazon_image_url,
    total_children,
    children,
    sync_status
  },
  productType
}) => {
  const { t } = useTranslation(['product_costs_and_settings'])
  const dispatch = useDispatch()
  const createNotification = useNotification()
  const queryClient = useQueryClient()

  const {
    amazon_seller_account_id,
    marketplace_id,
    country_code
  } = useSelectedAmazonSellerAccount()
  const { alertedProducts } = useSelector(state => state.productAlerts)

  const [syncTriggered, setSyncTriggered] = useState(false)
  const [loadSyncCount, setLoadSyncCount] = useState(false)
  const [syncStatus, setSyncStatus] = useState(sync_status)

  const isParent = productType === 'parent'

  useEffect(() => {
    const checkJob = () => {
      return api.getProductsResyncStatus({
        amazon_seller_account_id,
        marketplace_id,
        product_ids: [product_id]
      })
    }

    let isMounted = true

    const pollResult = async () => {
      if (!syncTriggered && syncStatus !== 1) return

      updateProductCostsAndSettingsCache([product_id], queryClient, {
        sync_status: 1
      })

      createNotification({
        message: t(
          'product_costs_and_settings:notification.syncProductSuccessMessage',
          'Your product sync is in progress!'
        ),
        level: 'success',
        title: t(
          'product_costs_and_settings:notification.syncProductSuccessTitle',
          'Product Sync Notice:'
        )
      })

      await poll(checkJob, checkSyncComplete).then(async response => {
        setSyncStatus(response.data[0].sync_status)

        updateProductCostsAndSettingsCache([product_id], queryClient, {
          sync_status: response.data[0].sync_status
        })

        queryClient.invalidateQueries([
          'api/junglekit/product/used_syncs_nums',
          {
            data: {
              amazon_seller_account_id,
              product_id
            }
          }
        ])
        if (isMounted) setSyncTriggered(false)
      })
    }

    pollResult()

    return () => {
      isMounted = false
    }
  }, [
    syncTriggered,
    syncStatus,
    amazon_seller_account_id,
    marketplace_id,
    product_id,
    queryClient,
    createNotification,
    t
  ])

  const { mutate: resyncProduct, isLoading } = useMutation(
    'api/junglekit/product/resync',
    {
      onSuccess: () => {
        setSyncTriggered(true)

        sendSegmentTrackEvent('CTA Clicked', {
          text: 'Sync Product Icon',
          location: 'Product Settings',
          ASIN: asin
        })
      },
      onError: ({ error }) => {
        createNotification({
          message: error,
          level: 'error',
          title: t(
            'product_costs_and_settings:notification.syncProductError',
            'Sync Product Error'
          )
        })
      }
    }
  )

  const { mutate: resyncImage, isLoading: isImageResyncing } = useMutation(
    'api/junglekit/product/refresh_image',
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [PRODUCT_SETTINGS_PARENTS_ENDPOINT]
        })
        queryClient.invalidateQueries({
          queryKey: [PRODUCT_SETTINGS_CHILDREN_ENDPOINT]
        })
        createNotification({
          level: 'success',
          title: t(
            'product_costs_and_settings:notification.refreshImage',
            'Refresh Product Image'
          ),
          message: t(
            'product_costs_and_settings:notification.refreshImageSuccess',
            'Product Image Updated!'
          )
        })
      },
      onError: ({ error }) => {
        createNotification({
          message: error,
          level: 'error',
          title: t(
            'product_costs_and_settings:notification.refreshImageError',
            'Refresh Product Image Error'
          )
        })
      }
    }
  )

  const { data: syncCountData, isLoading: isLoadingSyncCount } = useQuery(
    [
      'api/junglekit/product/used_syncs_nums',
      {
        data: {
          amazon_seller_account_id,
          product_id
        }
      }
    ],
    {
      enabled: loadSyncCount
    }
  )

  function handleRefreshImageClick(e) {
    e.stopPropagation()

    resyncImage({
      data: {
        amazon_seller_account_id,
        product_id,
        with_additional_images: true
      },
      method: 'PATCH'
    })
    sendSegmentTrackEvent('CTA Clicked', {
      text: 'Sync Image Icon',
      location: 'Product Settings',
      ASIN: asin
    })
  }

  const formattedImage = useMemo(
    () => formatImageResolution(amazon_image_url),
    [amazon_image_url]
  )

  const formatBadgeNumber = childrenCount =>
    childrenCount > 1 ? childrenCount : 0

  // only "leaf" products (no variants) can be clicked
  const formatAsin = () => {
    if (productType === 'variant') {
      return `${asin} (${t(
        'product_costs_and_settings:Table.variant',
        'Variant'
      )})`
    }
    if (isParent) {
      return `(${t('product_costs_and_settings:Table.parent', 'Parent')})`
    }
    return asin
  }

  // only "leaf" products (no variants) can be clicked
  const isClickable = !canExpand

  const badgeNumber = formatBadgeNumber(children?.length || total_children)

  const pipelinesId = `${country_code}/${asin}`
  const alertedProduct = alertedProducts[pipelinesId] || {}
  const isActive = Boolean(alertedProduct.id)

  const isSyncing =
    syncStatus === 1 || isLoading || syncTriggered || sync_status === 1
  const usedSyncs = syncCountData?.used || 0
  const totalSyncs = syncCountData?.total || 0

  return (
    <TitleCellWrapper>
      <StyledImageStack
        src={formattedImage}
        alt={title || 'n/a'}
        badgeNumber={badgeNumber}
        showStack={badgeNumber > 0}
        isImageResyncing={isImageResyncing}
        handleResync={handleRefreshImageClick}
      />

      <TitleWrapper>
        <TitleSection isProductVariant={productType === 'variant'}>
          <StyledTruncatedText>{title}</StyledTruncatedText>
          <SubtitleWrapper className={isClickable ? 'Clickable' : ''}>
            <FlagIcon country={country_code} size="16px" />
            <StyledASIN>{formatAsin()}</StyledASIN>
            {isParent ? null : (
              <>
                <Divider />
                <Tooltip side="right" content={sku} size="small">
                  <StyledSKU>{sku}</StyledSKU>
                </Tooltip>
              </>
            )}
          </SubtitleWrapper>
        </TitleSection>

        {isClickable ? (
          <>
            <StyledViewRowDataButton />
            <ActionsWrapper>
              <Tooltip
                side="top"
                content={
                  isSyncing
                    ? t(
                        'product_costs_and_settings:Table.syncInProgress',
                        'Sync in Progress'
                      )
                    : t(
                        'product_costs_and_settings:Table.syncsUsed',
                        `You have used {{usedSyncs}} of {{totalSyncs}} syncs today`,
                        { usedSyncs, totalSyncs }
                      )
                }
                size="auto">
                <div>
                  {isSyncing ? (
                    <LoadingIconWrapper>
                      <LoadingIcon color="grey600" size="20px" />
                    </LoadingIconWrapper>
                  ) : (
                    <div
                      onMouseEnter={() => {
                        if (isSyncing || isLoadingSyncCount) return
                        setLoadSyncCount(true)
                      }}
                      onMouseLeave={() => {
                        if (loadSyncCount) {
                          setLoadSyncCount(false)
                        }
                      }}>
                      <ClickableIcon
                        name="REFRESH"
                        padding="4px"
                        onClick={e => {
                          e.stopPropagation()

                          resyncProduct({
                            data: {
                              amazon_seller_account_id,
                              product_id
                            },
                            method: 'PATCH'
                          })
                        }}
                      />
                    </div>
                  )}
                </div>
              </Tooltip>
              <div>
                {alertedProduct?.isLoading ? (
                  <LoadingIconWrapper>
                    <LoadingIcon color="grey600" size="20px" />
                  </LoadingIconWrapper>
                ) : (
                  <Tooltip
                    side="top"
                    content={
                      isActive
                        ? t(
                            'product_costs_and_settings:Table.alertOff',
                            'Turn off product Alert'
                          )
                        : t(
                            'product_costs_and_settings:Table.alertOn',
                            'Turn on product Alert'
                          )
                    }
                    size="auto">
                    <span>
                      <ClickableIcon
                        name={isActive ? 'BELL_FILLED' : 'BELL_OFF'}
                        padding="4px"
                        onClick={e => {
                          e.stopPropagation()

                          sendSegmentTrackEvent('CTA Clicked', {
                            text: 'Product Alert Icon',
                            location: 'Product Settings',
                            ASIN: asin
                          })

                          const productAlert = {
                            id: pipelinesId,
                            country: country_code,
                            asin,
                            sku,
                            amazon_seller_account_id,
                            source: 'my_products'
                          }

                          isActive
                            ? dispatch(
                                removeProductAlert(
                                  alertedProduct.id,
                                  pipelinesId
                                )
                              )
                            : dispatch(addProductAlert(productAlert))
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            </ActionsWrapper>
          </>
        ) : null}
      </TitleWrapper>
    </TitleCellWrapper>
  )
}

ProductTitleCell.propTypes = {
  product: PropTypes.objectOf(PropTypes.any).isRequired,
  productType: PropTypes.oneOf(['parent', 'variant', 'nonparent']).isRequired
}
